import React from "react";
import {
  Box,
  Typography,
  Button,
  Stack,
  Avatar,
  CircularProgress,
  Alert,
  Paper,
} from "@mui/material";
import { Add, VisibilityRounded } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import Table, {
  TableHeaderConfig,
  TableRow,
  TableSortState,
  SortDirection,
  StatusDot,
} from "../components/Table";
import { type User } from "../utils/api";
import { useAppDispatch, useAppSelector } from "../store/hooks";
import { fetchUsersThunk } from "../features/users/usersSlice";
import FilterBar, {
  type FilterChipConfig,
  type FilterGroupConfig,
} from "../components/FilterBar";

const headers: TableHeaderConfig[] = [
  { key: "member", label: "User", sortable: true, sortKey: "name" },
  {
    key: "emailLabel",
    label: "Email Address",
    sortable: true,
    sortKey: "email",
  },
  { key: "roleLabel", label: "Role", sortable: true, sortKey: "role" },
  { key: "statusLabel", label: "Status", sortable: true, sortKey: "status" },
  {
    key: "created",
    label: "Created Date",
    sortable: true,
    sortKey: "createdAt",
  },
  { key: "actions", label: "Actions", align: "right" },
];

const formatDateTime = (value?: string) => {
  if (!value) {
    return "—";
  }
  return dayjs(value).format("MMM D, YYYY");
};

const getInitials = (name?: string) => {
  if (!name) {
    return "••";
  }
  return name
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((word) => word[0]?.toUpperCase())
    .join("");
};

const UserListPage: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const {
    data: users,
    status,
    error,
  } = useAppSelector((state) => state.users.list);
  const [searchQuery, setSearchQuery] = React.useState("");
  const [nameFilters, setNameFilters] = React.useState<string[]>([]);
  const [emailFilters, setEmailFilters] = React.useState<string[]>([]);
  const [sortState, setSortState] = React.useState<TableSortState>({
    key: "createdAt",
    direction: "desc",
  });

  const handleAddUser = () => {
    navigate("/user/new");
  };

  const nameFilterOptions = React.useMemo(() => {
    const uniqueNames = Array.from(
      new Set(
        users
          .map((user) => user.name)
          .filter((name): name is string => Boolean(name)),
      ),
    );
    return uniqueNames.map((name) => ({ label: name, value: name }));
  }, [users]);

  const emailFilterOptions = React.useMemo(() => {
    const uniqueEmails = Array.from(
      new Set(
        users
          .map((user) => user.email)
          .filter((email): email is string => Boolean(email)),
      ),
    );
    return uniqueEmails.map((email) => ({ label: email, value: email }));
  }, [users]);

  const filterGroups: FilterGroupConfig[] = React.useMemo(
    () => [
      {
        id: "names",
        label: "Names",
        options: nameFilterOptions,
        selectedValues: nameFilters,
        onChange: (values) => setNameFilters(values),
      },
      {
        id: "emails",
        label: "Emails",
        options: emailFilterOptions,
        selectedValues: emailFilters,
        onChange: (values) => setEmailFilters(values),
      },
    ],
    [emailFilterOptions, nameFilterOptions, emailFilters, nameFilters],
  );

  const filterChips: FilterChipConfig[] = React.useMemo(() => {
    const chips: FilterChipConfig[] = [];
    nameFilters.forEach((value) => {
      chips.push({
        id: `name-${value}`,
        label: `Name: ${value}`,
        onDelete: () =>
          setNameFilters((prev) => prev.filter((current) => current !== value)),
      });
    });

    emailFilters.forEach((value) => {
      chips.push({
        id: `email-${value}`,
        label: `Email: ${value}`,
        onDelete: () =>
          setEmailFilters((prev) =>
            prev.filter((current) => current !== value),
          ),
      });
    });

    return chips;
  }, [emailFilters, nameFilters]);

  const loadUsers = React.useCallback(
    () => dispatch(fetchUsersThunk()),
    [dispatch],
  );

  React.useEffect(() => {
    if (status === "idle") {
      void loadUsers();
    }
  }, [status, loadUsers]);

  const showInitialLoader =
    users.length === 0 && (status === "idle" || status === "loading");

  const clearFilters = () => {
    setNameFilters([]);
    setEmailFilters([]);
    setSearchQuery("");
    setSortState({ key: "createdAt", direction: "desc" });
  };

  const handleSortChange = React.useCallback(
    (key: string, direction: SortDirection) => {
      setSortState({ key: direction ? key : null, direction });
    },
    [],
  );

  const filteredUsers = React.useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    return users.filter((user) => {
      if (query) {
        const haystack = `${user.name ?? ""} ${user.email ?? ""} ${
          user.phoneNumber ?? ""
        } ${user.address ?? ""}`.toLowerCase();
        if (!haystack.includes(query)) {
          return false;
        }
      }

      if (
        nameFilters.length > 0 &&
        (!user.name || !nameFilters.includes(user.name))
      ) {
        return false;
      }

      if (
        emailFilters.length > 0 &&
        (!user.email || !emailFilters.includes(user.email))
      ) {
        return false;
      }

      return true;
    });
  }, [users, searchQuery, nameFilters, emailFilters]);

  const extractSortValue = React.useCallback(
    (user: User, key: string | null) => {
      if (!key) {
        return "";
      }
      switch (key) {
        case "name":
          return user.name ?? "";
        case "email":
          return user.email ?? "";
        case "role":
          return user.role ?? "";
        case "createdAt":
          return dayjs(user.createdAt).valueOf();
        default:
          return "";
      }
    },
    [],
  );

  const processedUsers = React.useMemo(() => {
    if (!sortState.direction || !sortState.key) {
      return filteredUsers;
    }
    const directionFactor = sortState.direction === "asc" ? 1 : -1;
    return [...filteredUsers].sort((a, b) => {
      const aValue = extractSortValue(a, sortState.key);
      const bValue = extractSortValue(b, sortState.key);
      if (typeof aValue === "number" && typeof bValue === "number") {
        return (aValue - bValue) * directionFactor;
      }
      const aText = aValue?.toString().toLowerCase() ?? "";
      const bText = bValue?.toString().toLowerCase() ?? "";
      return aText.localeCompare(bText) * directionFactor;
    });
  }, [extractSortValue, filteredUsers, sortState]);

  const getRoleColor = (role: string) => {
    switch (role?.toLowerCase()) {
      case "admin":
        return "#2563eb";
      case "super_admin":
        return "#7c3aed";
      default:
        return "#10b981";
    }
  };

  const getStatusColor = (status?: string) => {
    switch (status?.toLowerCase()) {
      case "active":
        return "#10b981";
      case "pending":
        return "#f59e0b";
      case "inactive":
        return "#ef4444";
      default:
        return "#10b981"; // Default to green/active for premium look
    }
  };

  const rows: TableRow[] = React.useMemo(
    () =>
      processedUsers.map((user) => ({
        member: (
          <Stack direction="row" spacing={1.5} alignItems="center">
            <Avatar
              src={user.imageUrl}
              sx={{
                width: 32,
                height: 32,
                fontSize: "0.75rem",
                fontWeight: 700,
              }}
            >
              {getInitials(user.name)}
            </Avatar>
            <Box>
              <Typography
                variant="body2"
                fontWeight={700}
                color="text.primary"
                onClick={() => navigate(`/user/${user._id}`)}
                sx={{
                  cursor: "pointer",
                  "&:hover": { textDecoration: "underline" },
                  textDecoration: "underline",
                }}
              >
                {user.name}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {user.phoneNumber || "No phone on file"}
              </Typography>
            </Box>
          </Stack>
        ),
        emailLabel: (
          <Box>
            <Typography variant="body2" fontWeight={500}>
              {user.email}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {user.address || "No address on file"}
            </Typography>
          </Box>
        ),
        roleLabel: (
          <StatusDot
            color={getRoleColor(user.role)}
            label={user.role === "admin" ? "Administrator" : "Member"}
          />
        ),
        statusLabel: (
          <StatusDot
            color={getStatusColor((user as any).status || "Active")}
            label={(user as any).status || "Active"}
          />
        ),
        created: (
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ fontWeight: 500 }}
          >
            {formatDateTime(user.createdAt)}
          </Typography>
        ),
        actions: (
          <Button
            variant="outlined"
            size="small"
            startIcon={
              <VisibilityRounded sx={{ fontSize: "16px !important" }} />
            }
            onClick={() => navigate(`/user/${user._id}`)}
            sx={{
              borderRadius: 1,
              textTransform: "none",
              fontWeight: 700,
              fontSize: "0.75rem",
              py: 0.5,
              borderColor: "divider",
            }}
          >
            View
          </Button>
        ),
      })),
    [navigate, processedUsers],
  );

  const gridTemplate = "2.5fr 2fr 1.2fr 1fr 1.5fr 1fr";

  return (
    <Box>
      <Box
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        flexWrap="wrap"
        gap={2}
        mb={3}
      >
        <FilterBar
          filterGroups={filterGroups}
          searchValue={searchQuery}
          onSearchChange={setSearchQuery}
          searchPlaceholder="Search by name, email, phone..."
          chips={filterChips}
          onClearAll={filterChips.length > 0 ? clearFilters : undefined}
        />
        <Button variant="contained" onClick={handleAddUser}>
          <Add sx={{ fontSize: "20px", verticalAlign: "middle" }} /> Add New
          User
        </Button>
      </Box>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {showInitialLoader ? (
        <Paper sx={{ p: 6, display: "flex", justifyContent: "center" }}>
          <CircularProgress />
        </Paper>
      ) : (
        <Table
          headers={headers}
          data={rows}
          getRowKey={(_, index) => processedUsers[index]?._id ?? index}
          emptyState="No users yet. Invite your first teammate."
          sortState={sortState}
          onSortChange={handleSortChange}
          gridTemplateColumns={gridTemplate}
          variant="cards"
        />
      )}
    </Box>
  );
};

export default UserListPage;
